import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LayoutDashboard, Package, ShoppingCart, Users, Ticket, Zap, Plus, Pencil, Trash2, X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { api, inr, errMsg, fmtDate } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { statusChip, statusLabel } from "./OrdersPage";

const STATUSES = ["placed", "confirmed", "preparing", "shipped", "out_for_delivery", "delivered", "cancelled"];
const EMPTY_PRODUCT = { name: "", category: "personalized-gifts", occasions: [], price: "", original_price: "", stock: "", description: "", highlights: "", images: "", delivery_types: ["standard", "express"], tags: "", personalized: false, active: true };
const EMPTY_COUPON = { code: "", type: "percent", value: "", min_order: 0, expiry: "", usage_limit: 1000, active: true, description: "" };
const EMPTY_SALE = { name: "", start: "", end: "", active: true, items: [] };

const inputCls = "h-10 px-3.5 rounded-xl border border-stone-200 bg-white text-sm outline-none focus:border-[#B45309] w-full";

function Modal({ title, onClose, children, wide }) {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[80] bg-stone-900/50 backdrop-blur-sm grid place-items-center p-4 overflow-y-auto" onClick={onClose} data-testid="admin-modal">
      <motion.div initial={{ scale: 0.95, y: 16 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 16 }} className={`bg-white rounded-3xl w-full ${wide ? "max-w-2xl" : "max-w-md"} p-6 sm:p-8 shadow-2xl my-8`} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-serif text-xl font-bold text-stone-900">{title}</h3>
          <button onClick={onClose} aria-label="Close" className="p-2 text-stone-400 hover:text-stone-700" data-testid="modal-close"><X size={18} /></button>
        </div>
        {children}
      </motion.div>
    </motion.div>
  );
}

export default function AdminPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("dashboard");
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [users, setUsers] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [sales, setSales] = useState([]);
  const [orderQ, setOrderQ] = useState("");
  const [meta, setMeta] = useState({ categories: [], occasions: [] });
  const [editProduct, setEditProduct] = useState(null); // null | 'new' | product
  const [editCoupon, setEditCoupon] = useState(null);
  const [editSale, setEditSale] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/admin/products").then(({ data }) => setProducts(data.items)).catch(() => {});
    api.get("/admin/orders").then(({ data }) => setOrders(data.items)).catch(() => {});
    api.get("/admin/users").then(({ data }) => setUsers(data.items)).catch(() => {});
    api.get("/admin/coupons").then(({ data }) => setCoupons(data.items)).catch(() => {});
    api.get("/admin/flash-sales").then(({ data }) => setSales(data.items)).catch(() => {});
    api.get("/meta").then(({ data }) => setMeta(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user === false) return nav("/auth", { state: { from: "/admin" } });
    if (user && user.role !== "admin") return nav("/");
    if (user) load();
  }, [user, nav, load]);

  if (!user || user.role !== "admin") return null;

  const searchOrders = async (q) => {
    setOrderQ(q);
    const { data } = await api.get("/admin/orders", { params: { q: q || undefined } });
    setOrders(data.items);
  };

  const updateStatus = async (orderId, status) => {
    try {
      await api.put(`/admin/orders/${orderId}/status`, { status });
      toast.success(`Order ${orderId} → ${statusLabel(status)}`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const toggleUser = async (u) => {
    try {
      const { data } = await api.put(`/admin/users/${u.user_id}/toggle`);
      toast.success(data.disabled ? "User disabled" : "User enabled");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const saveProduct = async (form) => {
    const payload = {
      ...form,
      price: Number(form.price), original_price: Number(form.original_price), stock: Number(form.stock),
      highlights: typeof form.highlights === "string" ? form.highlights.split("\n").filter(Boolean) : form.highlights,
      images: typeof form.images === "string" ? form.images.split("\n").filter(Boolean) : form.images,
      tags: typeof form.tags === "string" ? form.tags.split(",").map((t) => t.trim()).filter(Boolean) : form.tags,
    };
    if (editProduct === "new") await api.post("/admin/products", payload);
    else await api.put(`/admin/products/${editProduct.id}`, payload);
    toast.success("Product saved");
    setEditProduct(null);
    load();
  };

  const saveCoupon = async (form) => {
    const payload = { ...form, value: Number(form.value), min_order: Number(form.min_order), usage_limit: Number(form.usage_limit), expiry: new Date(form.expiry).toISOString() };
    if (editCoupon === "new") await api.post("/admin/coupons", payload);
    else await api.put(`/admin/coupons/${editCoupon.code}`, payload);
    toast.success("Coupon saved");
    setEditCoupon(null);
    load();
  };

  const saveSale = async (form) => {
    const payload = { ...form, start: new Date(form.start).toISOString(), end: new Date(form.end).toISOString(), items: form.items.map((i) => ({ ...i, flash_price: Number(i.flash_price), max_qty: Number(i.max_qty) })) };
    if (editSale === "new") await api.post("/admin/flash-sales", payload);
    else await api.put(`/admin/flash-sales/${editSale.id}`, payload);
    toast.success("Flash sale saved");
    setEditSale(null);
    load();
  };

  const TABS = [
    ["dashboard", "Dashboard", LayoutDashboard], ["products", "Products", Package], ["orders", "Orders", ShoppingCart],
    ["users", "Users", Users], ["coupons", "Coupons", Ticket], ["flash", "Flash Sales", Zap],
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-10" data-testid="admin-page">
      <h1 className="font-serif text-3xl sm:text-4xl font-bold text-stone-900 tracking-tight">Admin Dashboard</h1>
      <div className="flex gap-2 mt-6 overflow-x-auto no-scrollbar pb-1">
        {TABS.map(([k, l, Icon]) => (
          <button key={k} onClick={() => setTab(k)} className={`shrink-0 flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold transition-colors ${tab === k ? "bg-[#B45309] text-white" : "bg-white border border-stone-200 text-stone-600 hover:border-[#B45309]/40"}`} data-testid={`admin-tab-${k}`}>
            <Icon size={15} /> {l}
          </button>
        ))}
      </div>

      <div className="mt-8">
        {tab === "dashboard" && stats && (
          <div data-testid="admin-dashboard">
            <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
              {[["Total Users", stats.users], ["Products", stats.products], ["Total Orders", stats.orders], ["Revenue", inr(stats.revenue)], ["Pending Orders", stats.pending_orders], ["Delivered", stats.delivered_orders]].map(([l, v]) => (
                <div key={l} className="p-5 rounded-2xl bg-white border border-[#E9E2D7]">
                  <p className="text-[11px] font-bold uppercase tracking-wider text-stone-400">{l}</p>
                  <p className="font-serif text-2xl font-bold text-stone-900 mt-1.5">{v}</p>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
              <div className="p-6 rounded-2xl bg-white border border-[#E9E2D7]">
                <h3 className="font-serif text-lg font-bold mb-4">Recent orders</h3>
                {stats.recent_orders.length === 0 && <p className="text-sm text-stone-400">No orders yet.</p>}
                {stats.recent_orders.map((o) => (
                  <div key={o.order_id} className="flex items-center justify-between py-2.5 border-b border-stone-50 last:border-0 text-sm">
                    <span className="font-mono font-semibold">{o.order_id}</span>
                    <span className="text-stone-500">{o.customer?.name}</span>
                    <span className={`px-2.5 py-0.5 rounded-full text-[11px] font-bold ${statusChip(o.status)}`}>{statusLabel(o.status)}</span>
                    <span className="font-bold">{inr(o.total)}</span>
                  </div>
                ))}
              </div>
              <div className="p-6 rounded-2xl bg-white border border-[#E9E2D7]">
                <h3 className="font-serif text-lg font-bold mb-4">Top selling gifts</h3>
                {stats.top_products.map((p) => (
                  <div key={p.id} className="flex items-center gap-3 py-2.5 border-b border-stone-50 last:border-0 text-sm">
                    <img src={p.images?.[0]} alt={p.name} className="w-9 h-9 rounded-lg object-cover" />
                    <span className="flex-1 font-semibold text-stone-700 line-clamp-1">{p.name}</span>
                    <span className="text-stone-400">{p.sold_count} sold</span>
                    <span className={`text-xs font-bold ${p.stock <= 5 ? "text-red-600" : "text-stone-500"}`}>{p.stock} left</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {tab === "products" && (
          <div data-testid="admin-products">
            <div className="flex justify-between items-center mb-5">
              <p className="text-sm text-stone-500">{products.length} products</p>
              <button onClick={() => setEditProduct("new")} className="inline-flex items-center gap-2 px-5 h-10 rounded-full bg-[#B45309] text-white text-sm font-bold hover:bg-[#92400E]" data-testid="product-add-btn"><Plus size={15} /> Add Product</button>
            </div>
            <div className="bg-white rounded-2xl border border-[#E9E2D7] overflow-x-auto">
              <table className="w-full text-sm min-w-[720px]">
                <thead><tr className="text-left text-xs uppercase tracking-wider text-stone-400 border-b border-stone-100">
                  <th className="px-5 py-3.5">Product</th><th className="px-5 py-3.5">Category</th><th className="px-5 py-3.5">Price</th><th className="px-5 py-3.5">Stock</th><th className="px-5 py-3.5">Sold</th><th className="px-5 py-3.5 text-right">Actions</th>
                </tr></thead>
                <tbody>
                  {products.map((p) => (
                    <tr key={p.id} className="border-b border-stone-50 last:border-0 hover:bg-[#FDFBF7]" data-testid={`admin-product-${p.id}`}>
                      <td className="px-5 py-3"><div className="flex items-center gap-3"><img src={p.images?.[0]} alt="" className="w-10 h-10 rounded-lg object-cover" /><span className="font-semibold text-stone-800 line-clamp-1 max-w-[220px]">{p.name}</span></div></td>
                      <td className="px-5 py-3 text-stone-500 capitalize">{p.category.replace(/-/g, " ")}</td>
                      <td className="px-5 py-3 font-bold">{inr(p.price)}</td>
                      <td className={`px-5 py-3 font-semibold ${p.stock <= 5 ? "text-red-600" : "text-stone-600"}`}>{p.stock}</td>
                      <td className="px-5 py-3 text-stone-500">{p.sold_count}</td>
                      <td className="px-5 py-3"><div className="flex justify-end gap-2">
                        <button onClick={() => setEditProduct({ ...p, highlights: (p.highlights || []).join("\n"), images: (p.images || []).join("\n"), tags: (p.tags || []).join(", ") })} className="p-2 rounded-lg border border-stone-200 text-stone-500 hover:text-[#B45309] hover:border-[#B45309]" aria-label="Edit" data-testid={`product-edit-${p.id}`}><Pencil size={14} /></button>
                        <button onClick={async () => { await api.delete(`/admin/products/${p.id}`); toast.success("Product deleted"); load(); }} className="p-2 rounded-lg border border-stone-200 text-stone-500 hover:text-red-600 hover:border-red-300" aria-label="Delete" data-testid={`product-delete-${p.id}`}><Trash2 size={14} /></button>
                      </div></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "orders" && (
          <div data-testid="admin-orders">
            <input value={orderQ} onChange={(e) => searchOrders(e.target.value)} placeholder="Search by order ID, name or email…" className={`${inputCls} max-w-sm mb-5`} data-testid="admin-order-search" />
            <div className="bg-white rounded-2xl border border-[#E9E2D7] overflow-x-auto">
              <table className="w-full text-sm min-w-[760px]">
                <thead><tr className="text-left text-xs uppercase tracking-wider text-stone-400 border-b border-stone-100">
                  <th className="px-5 py-3.5">Order</th><th className="px-5 py-3.5">Customer</th><th className="px-5 py-3.5">Items</th><th className="px-5 py-3.5">Total</th><th className="px-5 py-3.5">Payment</th><th className="px-5 py-3.5">Status</th>
                </tr></thead>
                <tbody>
                  {orders.map((o) => (
                    <tr key={o.order_id} className="border-b border-stone-50 last:border-0" data-testid={`admin-order-${o.order_id}`}>
                      <td className="px-5 py-3"><p className="font-mono font-semibold">{o.order_id}</p><p className="text-xs text-stone-400">{fmtDate(o.created_at)}</p></td>
                      <td className="px-5 py-3"><p className="font-semibold text-stone-700">{o.customer?.name}</p><p className="text-xs text-stone-400">{o.customer?.email}</p></td>
                      <td className="px-5 py-3 text-stone-500 max-w-[180px]"><span className="line-clamp-1">{o.items.map((i) => `${i.name} ×${i.qty}`).join(", ")}</span></td>
                      <td className="px-5 py-3 font-bold">{inr(o.total)}</td>
                      <td className="px-5 py-3"><span className={`text-xs font-bold ${o.payment_status === "paid" ? "text-[#5c6b52]" : "text-amber-700"}`}>{o.payment_method.toUpperCase()} · {o.payment_status === "paid" ? "Paid" : "COD"}</span></td>
                      <td className="px-5 py-3">
                        <select value={o.status} onChange={(e) => updateStatus(o.order_id, e.target.value)} className="h-9 px-3 rounded-lg border border-stone-200 bg-white text-xs font-semibold outline-none" data-testid={`order-status-${o.order_id}`}>
                          {STATUSES.map((s) => <option key={s} value={s}>{statusLabel(s)}</option>)}
                        </select>
                      </td>
                    </tr>
                  ))}
                  {!orders.length && <tr><td colSpan={6} className="px-5 py-10 text-center text-stone-400">No orders found.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "users" && (
          <div className="bg-white rounded-2xl border border-[#E9E2D7] overflow-x-auto" data-testid="admin-users">
            <table className="w-full text-sm min-w-[640px]">
              <thead><tr className="text-left text-xs uppercase tracking-wider text-stone-400 border-b border-stone-100">
                <th className="px-5 py-3.5">User</th><th className="px-5 py-3.5">Phone</th><th className="px-5 py-3.5">Orders</th><th className="px-5 py-3.5">Joined</th><th className="px-5 py-3.5 text-right">Status</th>
              </tr></thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.user_id} className="border-b border-stone-50 last:border-0" data-testid={`admin-user-${u.user_id}`}>
                    <td className="px-5 py-3"><p className="font-semibold text-stone-800">{u.name} {u.role === "admin" && <span className="text-[10px] font-bold text-[#B45309] ml-1">ADMIN</span>}</p><p className="text-xs text-stone-400">{u.email}</p></td>
                    <td className="px-5 py-3 text-stone-500">{u.phone || "—"}</td>
                    <td className="px-5 py-3 text-stone-500">{u.order_count}</td>
                    <td className="px-5 py-3 text-stone-500">{fmtDate(u.created_at)}</td>
                    <td className="px-5 py-3 text-right">
                      {u.role !== "admin" && (
                        <button onClick={() => toggleUser(u)} className={`px-4 h-8 rounded-full text-xs font-bold transition-colors ${u.disabled ? "bg-red-50 text-red-700 hover:bg-red-100" : "bg-[#78866B]/15 text-[#5c6b52] hover:bg-[#78866B]/25"}`} data-testid={`user-toggle-${u.user_id}`}>
                          {u.disabled ? "Disabled · Enable" : "Active · Disable"}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "coupons" && (
          <div data-testid="admin-coupons">
            <div className="flex justify-between items-center mb-5">
              <p className="text-sm text-stone-500">{coupons.length} coupons</p>
              <button onClick={() => setEditCoupon("new")} className="inline-flex items-center gap-2 px-5 h-10 rounded-full bg-[#B45309] text-white text-sm font-bold hover:bg-[#92400E]" data-testid="coupon-add-btn"><Plus size={15} /> Create Coupon</button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {coupons.map((c) => (
                <div key={c.code} className="p-5 rounded-2xl bg-white border border-[#E9E2D7]" data-testid={`admin-coupon-${c.code}`}>
                  <div className="flex items-start justify-between">
                    <p className="font-mono font-bold text-stone-900">{c.code}</p>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${c.active ? "bg-green-50 text-green-700" : "bg-stone-100 text-stone-500"}`}>{c.active ? "Active" : "Disabled"}</span>
                  </div>
                  <p className="text-sm text-stone-600 mt-2">{c.type === "percent" ? `${c.value}% off` : `${inr(c.value)} off`} · min {inr(c.min_order)}</p>
                  <p className="text-xs text-stone-400 mt-1">Used {c.used_count}/{c.usage_limit} · expires {fmtDate(c.expiry)}</p>
                  <div className="flex gap-2 mt-4">
                    <button onClick={() => setEditCoupon({ ...c, expiry: c.expiry?.slice(0, 10) })} className="flex-1 h-9 rounded-full border border-stone-200 text-xs font-bold text-stone-600 hover:border-[#B45309] hover:text-[#B45309]" data-testid={`coupon-edit-${c.code}`}>Edit</button>
                    <button onClick={async () => { await api.delete(`/admin/coupons/${c.code}`); toast.success("Coupon deleted"); load(); }} className="flex-1 h-9 rounded-full border border-stone-200 text-xs font-bold text-stone-600 hover:border-red-300 hover:text-red-600" data-testid={`coupon-delete-${c.code}`}>Delete</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "flash" && (
          <div data-testid="admin-flash">
            <div className="flex justify-between items-center mb-5">
              <p className="text-sm text-stone-500">{sales.length} flash sales</p>
              <button onClick={() => setEditSale("new")} className="inline-flex items-center gap-2 px-5 h-10 rounded-full bg-[#B45309] text-white text-sm font-bold hover:bg-[#92400E]" data-testid="sale-add-btn"><Plus size={15} /> Create Flash Sale</button>
            </div>
            <div className="space-y-4">
              {sales.map((s) => (
                <div key={s.id} className="p-5 rounded-2xl bg-white border border-[#E9E2D7]" data-testid={`admin-sale-${s.id}`}>
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <p className="font-serif text-lg font-bold text-stone-900 flex items-center gap-2"><Zap size={16} className="text-[#B45309]" /> {s.name}</p>
                      <p className="text-xs text-stone-400 mt-1">{new Date(s.start).toLocaleString("en-IN")} → {new Date(s.end).toLocaleString("en-IN")} · {s.items.length} products</p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-bold ${{ active: "bg-green-50 text-green-700", upcoming: "bg-blue-50 text-blue-700", expired: "bg-stone-100 text-stone-500", disabled: "bg-red-50 text-red-600" }[s.status]}`} data-testid={`sale-status-${s.id}`}>{s.status}</span>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-3">
                    {s.items.map((i) => (
                      <span key={i.product_id} className="px-3 py-1.5 rounded-full bg-[#F5EFE6] text-xs font-semibold text-stone-600">{i.product_id} · {inr(i.flash_price)} · {i.sold_qty}/{i.max_qty} sold</span>
                    ))}
                  </div>
                  <div className="flex gap-2 mt-4">
                    <button onClick={() => setEditSale({ ...s, start: s.start.slice(0, 16), end: s.end.slice(0, 16) })} className="px-5 h-9 rounded-full border border-stone-200 text-xs font-bold text-stone-600 hover:border-[#B45309] hover:text-[#B45309]" data-testid={`sale-edit-${s.id}`}>Edit</button>
                    <button onClick={async () => { await api.delete(`/admin/flash-sales/${s.id}`); toast.success("Sale deleted"); load(); }} className="px-5 h-9 rounded-full border border-stone-200 text-xs font-bold text-stone-600 hover:border-red-300 hover:text-red-600" data-testid={`sale-delete-${s.id}`}>Delete</button>
                  </div>
                </div>
              ))}
              {!sales.length && <p className="text-sm text-stone-400 py-8 text-center">No flash sales yet — create one to light up the homepage.</p>}
            </div>
          </div>
        )}
      </div>

      <AnimatePresence>
        {editProduct && <ProductModal initial={editProduct === "new" ? EMPTY_PRODUCT : editProduct} meta={meta} onClose={() => setEditProduct(null)} onSave={saveProduct} />}
        {editCoupon && <CouponModal initial={editCoupon === "new" ? EMPTY_COUPON : editCoupon} onClose={() => setEditCoupon(null)} onSave={saveCoupon} />}
        {editSale && <SaleModal initial={editSale === "new" ? EMPTY_SALE : editSale} products={products} onClose={() => setEditSale(null)} onSave={saveSale} />}
      </AnimatePresence>
    </div>
  );
}

function ProductModal({ initial, meta, onClose, onSave }) {
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await onSave(form); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Modal title={initial.name ? "Edit Product" : "Add Product"} onClose={onClose} wide>
      <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="product-form">
        <input required value={form.name} onChange={set("name")} placeholder="Product name" className={inputCls} data-testid="product-form-name" />
        <select value={form.category} onChange={set("category")} className={inputCls} data-testid="product-form-category">
          {meta.categories.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
        </select>
        <input required type="number" min="1" value={form.price} onChange={set("price")} placeholder="Selling price ₹" className={inputCls} data-testid="product-form-price" />
        <input required type="number" min="1" value={form.original_price} onChange={set("original_price")} placeholder="Original price ₹" className={inputCls} data-testid="product-form-original" />
        <input required type="number" min="0" value={form.stock} onChange={set("stock")} placeholder="Stock" className={inputCls} data-testid="product-form-stock" />
        <input value={form.tags} onChange={set("tags")} placeholder="Tags (comma separated)" className={inputCls} data-testid="product-form-tags" />
        <textarea value={form.description} onChange={set("description")} placeholder="Description" rows={3} className="sm:col-span-2 px-3.5 py-3 rounded-xl border border-stone-200 bg-white text-sm outline-none focus:border-[#B45309] resize-none" data-testid="product-form-description" />
        <textarea value={form.images} onChange={set("images")} placeholder="Image URLs (one per line)" rows={2} className="sm:col-span-2 px-3.5 py-3 rounded-xl border border-stone-200 bg-white text-sm outline-none focus:border-[#B45309] resize-none" data-testid="product-form-images" />
        <textarea value={form.highlights} onChange={set("highlights")} placeholder="Highlights (one per line)" rows={2} className="sm:col-span-2 px-3.5 py-3 rounded-xl border border-stone-200 bg-white text-sm outline-none focus:border-[#B45309] resize-none" data-testid="product-form-highlights" />
        <div className="sm:col-span-2">
          <p className="text-xs font-bold text-stone-500 mb-2">Occasions</p>
          <div className="flex flex-wrap gap-2">
            {meta.occasions.map((o) => (
              <button type="button" key={o.slug} onClick={() => setForm({ ...form, occasions: form.occasions.includes(o.slug) ? form.occasions.filter((x) => x !== o.slug) : [...form.occasions, o.slug] })} className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${form.occasions.includes(o.slug) ? "bg-[#B45309] text-white border-[#B45309]" : "border-stone-200 text-stone-500"}`} data-testid={`product-form-occasion-${o.slug}`}>{o.name}</button>
            ))}
          </div>
        </div>
        <div className="sm:col-span-2 flex flex-wrap gap-4 text-sm text-stone-600">
          {["standard", "express", "same_day"].map((d) => (
            <label key={d} className="flex items-center gap-2"><input type="checkbox" checked={form.delivery_types.includes(d)} onChange={(e) => setForm({ ...form, delivery_types: e.target.checked ? [...form.delivery_types, d] : form.delivery_types.filter((x) => x !== d) })} className="accent-[#B45309]" data-testid={`product-form-delivery-${d}`} /> {d.replace("_", " ")}</label>
          ))}
          <label className="flex items-center gap-2"><input type="checkbox" checked={form.personalized} onChange={set("personalized")} className="accent-[#B45309]" data-testid="product-form-personalized" /> Personalizable</label>
          <label className="flex items-center gap-2"><input type="checkbox" checked={form.active} onChange={set("active")} className="accent-[#B45309]" /> Active</label>
        </div>
        <button disabled={busy} className="sm:col-span-2 h-11 rounded-full bg-[#B45309] text-white text-sm font-bold hover:bg-[#92400E] disabled:opacity-60" data-testid="product-form-save">{busy ? "Saving…" : "Save Product"}</button>
      </form>
    </Modal>
  );
}

function CouponModal({ initial, onClose, onSave }) {
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await onSave(form); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Modal title={initial.code ? "Edit Coupon" : "Create Coupon"} onClose={onClose}>
      <form onSubmit={submit} className="space-y-3" data-testid="coupon-form">
        <input required value={form.code} onChange={set("code")} placeholder="Code (e.g. DIWALI25)" className={`${inputCls} uppercase`} data-testid="coupon-form-code" />
        <div className="grid grid-cols-2 gap-3">
          <select value={form.type} onChange={set("type")} className={inputCls} data-testid="coupon-form-type"><option value="percent">% Percent</option><option value="fixed">₹ Fixed</option></select>
          <input required type="number" min="1" value={form.value} onChange={set("value")} placeholder="Value" className={inputCls} data-testid="coupon-form-value" />
          <input type="number" min="0" value={form.min_order} onChange={set("min_order")} placeholder="Min order ₹" className={inputCls} data-testid="coupon-form-min" />
          <input required type="date" value={form.expiry} onChange={set("expiry")} className={inputCls} data-testid="coupon-form-expiry" />
        </div>
        <input type="number" min="1" value={form.usage_limit} onChange={set("usage_limit")} placeholder="Usage limit" className={inputCls} data-testid="coupon-form-limit" />
        <input value={form.description} onChange={set("description")} placeholder="Description shown to customers" className={inputCls} data-testid="coupon-form-description" />
        <label className="flex items-center gap-2 text-sm text-stone-600"><input type="checkbox" checked={form.active} onChange={set("active")} className="accent-[#B45309]" data-testid="coupon-form-active" /> Active</label>
        <button disabled={busy} className="w-full h-11 rounded-full bg-[#B45309] text-white text-sm font-bold hover:bg-[#92400E] disabled:opacity-60" data-testid="coupon-form-save">{busy ? "Saving…" : "Save Coupon"}</button>
      </form>
    </Modal>
  );
}

function SaleModal({ initial, products, onClose, onSave }) {
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [picker, setPicker] = useState("");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const addItem = () => {
    if (!picker || form.items.some((i) => i.product_id === picker)) return;
    const p = products.find((x) => x.id === picker);
    setForm({ ...form, items: [...form.items, { product_id: picker, flash_price: Math.round(p.price * 0.8), max_qty: Math.min(p.stock, 10) }] });
    setPicker("");
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await onSave(form); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <Modal title={initial.name ? "Edit Flash Sale" : "Create Flash Sale"} onClose={onClose} wide>
      <form onSubmit={submit} className="space-y-3" data-testid="sale-form">
        <input required value={form.name} onChange={set("name")} placeholder="Sale name" className={inputCls} data-testid="sale-form-name" />
        <div className="grid grid-cols-2 gap-3">
          <div><label className="text-xs font-bold text-stone-500">Starts</label><input required type="datetime-local" value={form.start} onChange={set("start")} className={inputCls} data-testid="sale-form-start" /></div>
          <div><label className="text-xs font-bold text-stone-500">Ends</label><input required type="datetime-local" value={form.end} onChange={set("end")} className={inputCls} data-testid="sale-form-end" /></div>
        </div>
        <div className="flex gap-2">
          <select value={picker} onChange={(e) => setPicker(e.target.value)} className={inputCls} data-testid="sale-form-picker">
            <option value="">Select a product…</option>
            {products.map((p) => <option key={p.id} value={p.id}>{p.name} ({inr(p.price)} · {p.stock} in stock)</option>)}
          </select>
          <button type="button" onClick={addItem} className="shrink-0 h-10 px-4 rounded-xl bg-stone-900 text-white text-xs font-bold" data-testid="sale-form-add-product">Add</button>
        </div>
        <div className="space-y-2">
          {form.items.map((i, idx) => (
            <div key={i.product_id} className="flex items-center gap-2 p-3 rounded-xl bg-[#F9F3EA] text-sm">
              <span className="flex-1 font-semibold text-stone-700 truncate">{i.product_id}</span>
              <input type="number" min="1" value={i.flash_price} onChange={(e) => setForm({ ...form, items: form.items.map((x, xi) => xi === idx ? { ...x, flash_price: e.target.value } : x) })} className="w-24 h-9 px-2 rounded-lg border border-stone-200 text-xs" placeholder="Flash ₹" data-testid={`sale-item-price-${idx}`} />
              <input type="number" min="1" value={i.max_qty} onChange={(e) => setForm({ ...form, items: form.items.map((x, xi) => xi === idx ? { ...x, max_qty: e.target.value } : x) })} className="w-20 h-9 px-2 rounded-lg border border-stone-200 text-xs" placeholder="Max qty" data-testid={`sale-item-qty-${idx}`} />
              <button type="button" onClick={() => setForm({ ...form, items: form.items.filter((_, xi) => xi !== idx) })} aria-label="Remove" className="text-stone-400 hover:text-red-600"><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
        <label className="flex items-center gap-2 text-sm text-stone-600"><input type="checkbox" checked={form.active} onChange={set("active")} className="accent-[#B45309]" data-testid="sale-form-active" /> Active</label>
        <button disabled={busy} className="w-full h-11 rounded-full bg-[#B45309] text-white text-sm font-bold hover:bg-[#92400E] disabled:opacity-60" data-testid="sale-form-save">{busy ? "Saving…" : "Save Flash Sale"}</button>
      </form>
    </Modal>
  );
}
